import { pick, uniqBy } from "lodash";
import { toPascalCase } from "../../shared/utils/string";

import { Model } from "../../shared/models/model";

import { StartNode } from "./node/start-node";

import { AgeValidationNode } from "./node/age-validation-node";
import { InfoNode } from "./node/info-node";
import { QuestionNode } from "./node/question-node";
import { SelectPatientTypeNode } from "./node/select-patient-type-node";
import { SelectStateNode } from "./node/select-state-node";
import { SelectVisitTypeNode } from "./node/select-visit-type-node";

import { BroadcastNode } from "./node/broadcast-node";
import { ChangeVisitTypeNode } from "./node/change-visit-type-node";
import { ConditionNode } from "./node/condition-node";

import { SwitchTreeNode } from "./node/switch-tree-node";
import { SwitchProviderNode } from "./node/switch-provider-node";

import { ContinueNode } from "./node/continue-node";
import { DenyNode } from "./node/deny-node";

import { AllowBookingNode } from "./node/allow-booking-node";
import { MissingVisitTypeNode } from "./node/missing-visit-type-node";

import { useVisitTypeStore } from "../store/visit-type";

const nodes = {
  StartNode,

  InfoNode,
  SelectPatientTypeNode,
  AgeValidationNode,
  SelectVisitTypeNode,
  SelectStateNode,
  QuestionNode,

  BroadcastNode,
  ChangeVisitTypeNode,
  ConditionNode,

  SwitchTreeNode,
  SwitchProviderNode,

  DenyNode,
  ContinueNode,

  AllowBookingNode,
  MissingVisitTypeNode,
};

export class DecisionTree extends Model {
  constructor(attributes) {
    if (attributes.nodes) {
      attributes.nodes = attributes.nodes.map((node) => {
        const modelName = toPascalCase(node.type) + "Node";

        return new nodes[modelName](node);
      });

      attributes.nodes.push(new ContinueNode({ id: "continue" }));
      attributes.nodes.push(new AllowBookingNode());
      attributes.nodes.push(new MissingVisitTypeNode());
    }

    super(attributes);

    this.createAdjacencyList();
  }

  get defaults() {
    return {
      nodes: undefined,
      appointmentSlot: undefined,
      defaultPatientType: "new",

      previousNodes: [],
      currentNode: undefined,
      started: false,
    };
  }

  get startNode() {
    return this.nodes.find((node) => node.isRootStart);
  }

  get currentNodeId() {
    return this.currentNode?.id;
  }

  get uniquePreviousNodes() {
    return uniqBy(this.previousNodes, "id");
  }

  get prevNode() {
    return this.previousNodes.at(-1);
  }

  get prevNodeId() {
    return this.prevNode?.id;
  }

  get nextNode() {
    return this.nodeById(this.nextNodeId);
  }

  get nextNodeId() {
    return this.currentNode.nextNodeId(this.context);
  }

  get isFirstStepNode() {
    return this.previousNodes.every((node, index) => {
      const context = this.contextForNodes(this.previousNodes.slice(0, index));

      return node.autoAdvance(context);
    });
  }

  get hasPossibleNextNodes() {
    // If we have no possible next nodes, we're at the end
    if (this.currentNode.possibleNextNodeIds.length === 0) {
      return false;
    }

    // If there are more step nodes, we're not at the end
    if (!this.isLastStepNode) {
      return true;
    }

    // If the last reachable node is an Allow Booking node, we're not at the end
    return this.lastReachableNode.isAllowBooking;
  }

  get isLastStepNode() {
    // If the current node is a Missing Visit Type, or Deny node, it's the last step
    if (this.currentNode.isMissingVisitType || this.currentNode.isDeny) {
      return true;
    }

    // If we have no reachable nodes, we haven't reached the end yet, we're just waiting for an answer to decide where to go next
    if (this.reachableNodes.length === 0) {
      return false;
    }

    // Otherwise we're at the last step if all the reachable nodes are auto-advancing
    // and the last one is a non-step Deny node or an Allow Booking node
    const interimReachableNodes = this.interimReachableNodes;
    const lastReachableNode = this.lastReachableNode;

    const allInterimNodesAutoAdvance = interimReachableNodes.every((node, index) => {
      const context = this.contextForNodes([
        ...this.previousNodes,
        this.currentNode,
        ...interimReachableNodes.slice(0, index),
      ]);

      return node.autoAdvance(context);
    });

    return allInterimNodesAutoAdvance && (lastReachableNode.isNonStepDeny || lastReachableNode.isAllowBooking);
  }

  get isLastStepNodeBeforeDenial() {
    return this.isLastStepNode && this.lastReachableNode?.isNonStepDeny;
  }

  // Broadcast nodes that we traversed through to get to the current node that want to be placed on the current node
  // We display these at the top of the current node's card
  get currentBroadcastsFromPreviousNodes() {
    return this.uniquePreviousNodes
      .filter((node) => node.isBroadcast && node.placementNodeIds.includes(this.currentNode.id))
      .map((node) => node.toProcessedData(this.contextWithCurrentNode));
  }

  // Broadcast nodes later in the tree that we can reach from the current node that want to be placed on the current node
  // We display these at the bottom of the current node's card
  get currentBroadcastsFromReachableNodes() {
    // Ignore broadcasts that were already placed at the top of this node by previously seen nodes
    const previouslyPlacedBroadcastIds = this.currentBroadcastsFromPreviousNodes.map((node) => node.id);

    const broadcastsForCurrentNode = new Set(
      this.reachableNodes.filter(
        (node) =>
          node.isBroadcast &&
          node.placementNodeIds.includes(this.currentNode.id) &&
          !previouslyPlacedBroadcastIds.includes(node.id),
      ),
    );

    // If we're on the last step node and the last node is a non-step Deny node,
    // we want to show all broadcasts placed on interim nodes
    if (this.isLastStepNodeBeforeDenial) {
      const broadcastsInPath = [...this.previousNodes, ...this.reachableNodes].filter((node) => node.isBroadcast);

      this.interimReachableNodes.forEach((node) => {
        broadcastsInPath.forEach((broadcast) => {
          if (broadcast.placementNodeIds.includes(node.id)) {
            broadcastsForCurrentNode.add(broadcast);
          }
        });
      });
    }

    return [...broadcastsForCurrentNode].map((node) => node.toProcessedData(this.contextWithCurrentNode));
  }

  get lastReachableNode() {
    return this.reachableNodes.at(-1);
  }

  get interimReachableNodes() {
    return this.reachableNodes.slice(0, -1);
  }

  get reachableNodes() {
    if (!this.nextNode) {
      return [];
    }

    const reachableNodes = [this.nextNode];

    while (true) {
      const reachableContext = this.contextForNodes([
        ...this.previousNodes,
        this.currentNode,
        ...reachableNodes.slice(0, -1),
      ]);

      const lastReachableNode = reachableNodes.at(-1);
      lastReachableNode.beforeShow(reachableContext);
      const nextNodeId = lastReachableNode.nextNodeId(reachableContext);

      if (!nextNodeId) {
        break;
      }

      reachableNodes.push(this.nodeById(nextNodeId));
    }

    return reachableNodes;
  }

  get nodeIds() {
    return this.nodes.map((node) => node.id);
  }

  get progressPercent() {
    const percent = 1 - this.longestRemainingPathLength / (this.longestRemainingPathLength + this.previousNodes.length);

    return Math.round(percent * 100) / 100;
  }

  get longestRemainingPathLength() {
    return this.findLongestPathLength(this.currentNode.id);
  }

  get isShowingStep() {
    return !this.isAutoAdvancing && !this.isAllowBooking;
  }

  get isAutoAdvancing() {
    return this.currentNode.autoAdvance(this.context);
  }

  get isAllowBooking() {
    return this.currentNode.isAllowBooking;
  }

  get isDeny() {
    return this.currentNode.isDeny || this.isLastStepNodeBeforeDenial;
  }

  get isMissingVisitType() {
    return this.currentNode.isMissingVisitType;
  }

  get patientType() {
    return this.context.patientType;
  }

  get switchProviderGroupIds() {
    return this.context.switchProviderGroupIds;
  }

  get visitTypeId() {
    return this.context.visitTypeId;
  }

  get visitType() {
    return this.context.visitType;
  }

  get contextWithCurrentNode() {
    return this.contextForNodes([...this.previousNodes, this.currentNode]);
  }

  get context() {
    return this.contextForNodes(this.previousNodes);
  }

  contextForNodes(nodes) {
    const context = this.undecoratedContextForNodes(nodes);

    const switchTreeExitNodeIds = [];
    nodes.forEach((node, index) => {
      if (node.isSwitchTree) {
        switchTreeExitNodeIds.push(node.targetNodeId);
      } else if (node.canForceSwitchTree) {
        const switchContext = this.undecoratedContextForNodes(nodes.slice(0, index));
        if (node.isDecisionTreeSwitch(switchContext)) {
          switchTreeExitNodeIds.length = 0;
        }
      } else if (node.isContinue) {
        switchTreeExitNodeIds.pop();
      }
    });
    context.switchTreeExitNodeId = switchTreeExitNodeIds.at(-1);

    context.defaultPatientType = this.defaultPatientType;

    if (context.visitTypeId) {
      const { visitTypes } = useVisitTypeStore();
      context.visitType = visitTypes.find((visitType) => visitType.id === context.visitTypeId);
      context.visitTypeMinimumAge = context.visitType?.minimumAge;
      context.visitTypeMaximumAge = context.visitType?.maximumAge;
      context.visitTypeVerifyProviderAgeRange = context.visitType?.verifyProviderAgeRange;
    }

    context.bookingVisitTypeId = this.bookingVisitTypeForNodes(nodes, context.visitType, context.patientType)?.id;

    return context;
  }

  undecoratedContextForNodes(nodes) {
    return nodes.reduce((context, node) => ({ ...context, ...node.context }), this.basicContext);
  }

  get basicContext() {
    return { decisionTreeId: this.id, ...this.providerContext };
  }

  get providerContext() {
    return pick(this.appointmentSlot, [
      "patientType",

      "visitTypeId",

      "providerVisitTypeIds",
      "providerSpecialtyIds",
      "providerLocationId",
      "providerNetworkIds",
      "providerTypeIds",
      "providerGroupIds",
      "providerLicensedStates",
      "providerLicensedStateNames",

      "providerMinimumAge",
      "providerMaximumAge",

      "providerName",
      "providerPhoneNumber",

      "providerType",
      "providerTypeDescription",

      "licensedProviderSearchUrl",
    ]);
  }

  get currentState() {
    const state = this.previousNodes.reduce(
      (currentState, node) => ({ ...currentState, [node.id]: node.currentState }),
      {},
    );
    state.currentNodeId = this.currentNode.id;

    return state;
  }

  get bookingProviderGroupIds() {
    return this.switchProviderGroupIds;
  }

  get bookingPatientType() {
    return this.patientType;
  }

  get bookingVisitTypeId() {
    return this.bookingVisitType?.id;
  }

  get bookingAgeRange() {
    if (this.context.inAgeRange && this.context.validateBirthdate) {
      return pick(this.context, ["minimumAge", "maximumAge"]);
    }

    return undefined;
  }

  get reviewStepBroadcasts() {
    return this.uniquePreviousNodes
      .filter((node) => node.isBroadcast && node.placementNodeIds.includes("review"))
      .map((node) => node.toProcessedData(this.contextWithCurrentNode));
  }

  get confirmationPageBroadcasts() {
    return this.uniquePreviousNodes
      .filter((node) => node.isBroadcast && node.placementNodeIds.includes("confirmation"))
      .map((node) => node.toProcessedData(this.contextWithCurrentNode));
  }

  // Returns the visit type that should be sent to the Appointment Booking Wizard
  get bookingVisitType() {
    return this.bookingVisitTypeForNodes(this.previousNodes, this.visitType, this.patientType);
  }

  bookingVisitTypeForNodes(nodes, visitType, patientType) {
    if (!visitType) {
      return undefined;
    }

    // If the last ChangeVisitType node's visit type matches what we currently have, use it without validating patient type
    const changeVisitTypeNode = nodes.findLast((node) => node.isChangeVisitType);
    if (changeVisitTypeNode && changeVisitTypeNode.visitTypeId === visitType.id) {
      return visitType;
    }

    // If the current visit type is valid for the patient type, use it
    if (visitType.validFor(patientType)) {
      return visitType;
    }

    // Otherwise, try to construct a valid matching visit type for the patient type
    return this.matchingProviderVisitType(visitType, patientType, this.providerContext.providerVisitTypeIds);
  }

  matchingProviderVisitType(visitType, patientType, providerVisitTypeIds) {
    if (!visitType || !patientType || !providerVisitTypeIds) {
      return undefined;
    }

    const { visitTypes } = useVisitTypeStore();

    // Start by finding all valid visit types that match the current visit type's name
    const matchingValidVisitTypes = visitTypes.filter(
      (v) => v.matchName === visitType.matchName && v.validFor(patientType),
    );

    // Option 0: Find an exactly matching valid visit type that the provider has
    const exactMatchingProviderVisitType = matchingValidVisitTypes.find(
      (v) => visitType.id === v.id && providerVisitTypeIds.includes(v.id),
    );

    if (exactMatchingProviderVisitType) {
      return exactMatchingProviderVisitType;
    }

    // Option 1: Find a matching valid visit type that the provider has
    const matchingProviderVisitType = matchingValidVisitTypes.find((v) => providerVisitTypeIds.includes(v.id));

    if (matchingProviderVisitType) {
      return matchingProviderVisitType;
    }

    // Option 2: Find a matching valid visit type that has the same specialty restrictions as the current visit type
    const matchingSpecialtyRestrictionVisitType = matchingValidVisitTypes.find((v) =>
      v.hasMatchingSpecialtyRestrictions(visitType),
    );

    if (matchingSpecialtyRestrictionVisitType) {
      return matchingSpecialtyRestrictionVisitType;
    }

    // Option 3: Find a matching valid visit type that has no specialty restrictions
    const matchingNoSpecialtyRestrictionVisitType = matchingValidVisitTypes.find((v) => v.hasNoSpecialtyRestrictions());

    return matchingNoSpecialtyRestrictionVisitType;
  }

  goToPrevNode() {
    this.currentNode = this.prevNode;
    this.previousNodes.pop();

    if (this.currentNode.autoAdvance(this.context)) {
      this.goToPrevNode();
    }
  }

  goToNextNode() {
    const previousNode = this.currentNode;
    const nextNode = this.nextNode;

    nextNode.beforeShow(this.contextWithCurrentNode);

    // Create a new instance of the next node, so that we don't mutate the original
    this.currentNode = new nodes[nextNode.name](nextNode.toJSON());

    this.previousNodes.push(previousNode);

    if (this.isAutoAdvancing) {
      this.goToNextNode();
    }
  }

  reset() {
    this.previousNodes = [];
    this.nodes.forEach((node) => node.reset());
    this.start();
  }

  start() {
    this.currentNode = this.startNode;

    if (this.isAutoAdvancing) {
      this.goToNextNode();
    }

    this.started = true;
  }

  setCurrentState(state) {
    this.reset();

    while (this.currentNode.id !== state.currentNodeId) {
      this.currentNode.setCurrentState(state[this.currentNode.id]);
      this.goToNextNode();
    }

    if (this.isAllowBooking) {
      this.goToPrevNode();
    }
  }

  nodeById(id) {
    return this.nodes.find((node) => node.id === id);
  }

  // Adjacency list used to find the longest remaining path in the decision tree
  createAdjacencyList() {
    this.adjacencyList = new Map(
      this.nodes.map(({ id, possibleNextNodeIds }) => [id, possibleNextNodeIds.filter(Boolean)]),
    );
  }

  findLongestPathLength(startNodeId) {
    const topologicalOrder = this.topologicalSort(startNodeId);
    const longestPath = new Map();
    longestPath.set(startNodeId, 0);

    for (const nodeId of topologicalOrder) {
      const neighbors = this.adjacencyList.get(nodeId);
      for (const neighborId of neighbors) {
        const currentLength = longestPath.get(nodeId);
        const neighborLength = longestPath.get(neighborId) || 0;
        longestPath.set(neighborId, Math.max(neighborLength, currentLength + 1));
      }
    }

    // Find the maximum value in the longestPath Map
    let maxLength = 0;
    for (const length of longestPath.values()) {
      maxLength = Math.max(maxLength, length);
    }

    return maxLength;
  }

  topologicalSort(startNodeId) {
    const stack = [];
    const visited = new Map();

    for (const nodeId of this.adjacencyList.keys()) {
      visited[nodeId] = false;
    }

    this.topologicalSortUtil(startNodeId, visited, stack);
    return stack.reverse();
  }

  topologicalSortUtil(nodeId, visited, stack) {
    visited[nodeId] = true;
    const neighbors = this.adjacencyList.get(nodeId);
    for (const neighbor of neighbors) {
      if (!visited[neighbor]) {
        this.topologicalSortUtil(neighbor, visited, stack);
      }
    }
    stack.push(nodeId);
  }
}
